import React, { useEffect, useState } from 'react';
import { fetchSkills, createSkill, updateSkill, deleteSkill } from '../services/skillService';
import { Button, Input, FileUpload, Loader } from '../components/common';
import { getImageUrl } from '../utils/imageUrl';
import AdminLayout from './AdminLayout';

const ManageSkills = () => {
    const [skills, setSkills] = useState([]);
    const [newSkill, setNewSkill] = useState({ name: '', image: '', percentage: '95%' });
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadSkills = async () => {
        setLoading(true);
        try {
            const { data } = await fetchSkills();
            setSkills(data);
        } catch (error) {
            console.error("Failed to fetch skills", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSkills();
    }, []);

    const handleEdit = (skill) => {
        setNewSkill(skill);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setNewSkill({ name: '', image: '', percentage: '95%' });
        setIsEditing(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEditing) {
                await updateSkill(newSkill._id, newSkill);
            } else {
                await createSkill(newSkill);
            }
            loadSkills();
            handleCancel();
        } catch (error) {
            console.error("Failed to save skill", error);
        } finally {
            setSaving(false);
        }
    };

    const deleteHandler = async (id) => {
        if (window.confirm('Are you sure you want to delete this skill?')) {
            try {
                await deleteSkill(id);
                loadSkills();
            } catch (error) {
                console.error("Failed to delete skill", error);
            }
        }
    };

    return (
        <AdminLayout title="Manage Skills">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6" id="admin-root">
                {/* Form Section */}
                <div className="lg:col-span-1">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 sticky top-6">
                        <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                            <h3 className="text-lg font-semibold text-white flex items-center">
                                <i className={`fas ${isEditing ? 'fa-edit text-yellow-500' : 'fa-plus-circle text-green-500'} mr-2`}></i>
                                {isEditing ? 'Edit Skill' : 'Add New Skill'}
                            </h3>
                            {isEditing && (
                                <button onClick={handleCancel} className="text-xs text-gray-400 hover:text-white transition-colors">
                                    <i className="fas fa-times mr-1"></i> Cancel
                                </button>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <Input
                                label="Skill Name"
                                name="name"
                                required
                                value={newSkill.name}
                                onChange={e => setNewSkill({ ...newSkill, name: e.target.value })}
                                placeholder="e.g. React js"
                            />

                            <Input
                                label="Percentage (%)"
                                name="percentage"
                                required
                                value={newSkill.percentage}
                                onChange={e => setNewSkill({ ...newSkill, percentage: e.target.value })}
                                placeholder="e.g. 95%"
                            />

                            <FileUpload
                                label="Skill Icon/Image"
                                value={newSkill.image}
                                onChange={imageUrl => setNewSkill({ ...newSkill, image: imageUrl })}
                            />

                            <Button
                                type="submit"
                                variant={isEditing ? 'warning' : 'primary'}
                                fullWidth
                                isLoading={saving}
                            >
                                {isEditing ? 'Update Skill' : 'Create Skill'}
                            </Button>
                        </form>
                    </div>
                </div>

                {/* Table Section */}
                <div className="lg:col-span-2">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden flex flex-col h-full">
                        <div className="px-6 py-4 border-b border-gray-700 bg-gray-800 flex justify-between items-center bg-gray-800/50 backdrop-blur-sm sticky top-0 z-20">
                            <h3 className="text-lg font-medium text-white flex items-center">
                                <i className="fas fa-list-ul mr-2 text-gray-400"></i> Existing Skills
                            </h3>
                            <span className="bg-gray-700 text-gray-300 py-1 px-2.5 rounded-full text-xs font-bold shadow-inner">
                                {skills.length}
                            </span>
                        </div>

                        <div className="flex-1 overflow-auto custom-scrollbar bg-gray-900/50">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-800 sticky top-0 z-10 shadow-md">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700">Skill</th>
                                        <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700">Percentage</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-gray-800 divide-y divide-gray-700">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-400">
                                                <Loader size="sm" text="Loading skills..." />
                                            </td>
                                        </tr>
                                    ) : skills.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-12 text-center text-sm text-gray-500 flex flex-col items-center">
                                                <i className="fas fa-tools text-3xl mb-2 opacity-30"></i>
                                                <p>No skills found.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        skills.map(skill => (
                                            <tr key={skill._id} className={`hover:bg-gray-700/50 transition-colors duration-150 group ${isEditing && newSkill._id === skill._id ? 'bg-indigo-900/20 border-l-2 border-indigo-500' : 'border-l-2 border-transparent'}`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10 bg-gray-900 rounded-md p-1 border border-gray-700 mr-3">
                                                            <img
                                                                className="h-full w-full object-contain"
                                                                src={getImageUrl(skill.image)}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <span className="text-sm font-semibold text-white group-hover:text-indigo-300 transition-colors">{skill.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-300">
                                                    {skill.percentage}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(skill)}
                                                        className="text-indigo-400 hover:text-indigo-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all border border-transparent hover:border-indigo-500/30"
                                                        title="Edit Skill"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => deleteHandler(skill._id)}
                                                        className="text-red-400 hover:text-red-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all border border-transparent hover:border-red-500/30"
                                                        title="Delete Skill"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ManageSkills;
